/**
 * MaintenanceReport — full-screen dialog surface for the monthly Maintenance
 * Report accessed via the "Maintenance Report" button on the Maintenance
 * page. Renders:
 *   - Month selector + refresh + CSV export dropdown
 *   - KPI cards with trend arrows vs previous month
 *   - Repair Item consumption table (top bikes/riders, spend/qty trends)
 *   - AMC vs Non-AMC breakdown
 *   - Cost recovery split: In-house (expandable per-installment history) +
 *     External (claim amounts)
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Download, RefreshCw, TrendingUp, TrendingDown, Minus, ChevronDown, ChevronRight,
  Wallet, Package, PieChart, Receipt, Users, History, X,
} from 'lucide-react';
import { CurrencyDisplay } from './DirhamSymbol';
import { API, getAuthHeader } from '../App';

const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const TrendPill = ({ pct, invertGood = false, size = 'sm' }) => {
  if (pct === null || pct === undefined) {
    return <span className="text-slate-400 text-[11px]">—</span>;
  }
  const up = pct > 0.05;
  const down = pct < -0.05;
  const good = invertGood ? down : up;
  const bad = invertGood ? up : down;
  const cls = good ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : bad ? 'text-rose-700 bg-rose-50 border-rose-200'
    : 'text-slate-600 bg-slate-100 border-slate-200';
  const Icon = up ? TrendingUp : down ? TrendingDown : Minus;
  return (
    <span className={`inline-flex items-center gap-0.5 rounded border px-1.5 py-0.5 font-medium ${cls} ${size === 'lg' ? 'text-xs' : 'text-[10px]'}`}>
      <Icon className={size === 'lg' ? 'w-3 h-3' : 'w-2.5 h-2.5'} />
      {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
    </span>
  );
};

const KpiCard = ({ label, value, isCurrency = true, trend, invertGood = false, icon: Icon, accent = 'blue' }) => {
  const bg = {
    blue: 'from-blue-500/10 to-blue-500/5 border-blue-200',
    emerald: 'from-emerald-500/10 to-emerald-500/5 border-emerald-200',
    amber: 'from-amber-500/10 to-amber-500/5 border-amber-200',
    rose: 'from-rose-500/10 to-rose-500/5 border-rose-200',
    violet: 'from-violet-500/10 to-violet-500/5 border-violet-200',
  }[accent];
  return (
    <Card className={`bg-gradient-to-br ${bg} border`}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
          <span className="uppercase tracking-wide font-semibold">{label}</span>
          {Icon && <Icon className="w-3.5 h-3.5" />}
        </div>
        <div className="flex items-baseline justify-between">
          <div className="text-lg font-bold text-slate-900">
            {isCurrency ? <CurrencyDisplay amount={value || 0} /> : (value || 0).toLocaleString()}
          </div>
          <TrendPill pct={trend} invertGood={invertGood} size="lg" />
        </div>
      </CardContent>
    </Card>
  );
};

const MaintenanceReport = ({ open, onOpenChange }) => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  // Repair-item history modal — shows every replacement of a specific
  // repair item across all bikes for the current month, together with
  // odometer readings and the "KM covered since previous replacement"
  // so the user can gauge spare-part life.
  const [historyItem, setHistoryItem] = useState(null);   // {item_id, item_code, item_name}
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const monthKey = `${year}-${String(month).padStart(2, '0')}`;

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/maintenance/report`, {
        params: { month, year },
        headers: { ...getAuthHeader() },
      });
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open, month, year]);

  const openItemHistory = async (item) => {
    setHistoryItem(item);
    setHistoryLoading(true);
    setHistoryData(null);
    try {
      const params = { month, year };
      if (item.item_id) params.item_id = item.item_id;
      else if (item.item_code) params.item_code = item.item_code;
      else if (item.item_name) params.item_name = item.item_name;
      const res = await axios.get(`${API}/maintenance/report/item-history`, {
        params,
        headers: { ...getAuthHeader() },
      });
      setHistoryData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load item history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeItemHistory = () => {
    setHistoryItem(null);
    setHistoryData(null);
  };

  const yearOptions = useMemo(() => {
    const y = now.getFullYear();
    return [y - 2, y - 1, y, y + 1];
  }, []);

  const toggleRow = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const downloadCsv = (type) => {
    const url = `${API}/maintenance/report/export.csv?type=${type}&month=${month}&year=${year}`;
    const token = localStorage.getItem('token') || localStorage.getItem('access_token') || '';
    // Auth header can't be attached to <a> href; fetch as blob then trigger download.
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `maintenance-report-${monthKey}-${type}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
      })
      .catch(() => toast.error('Download failed'));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[96vw] w-[96vw] h-[92vh] p-0 flex flex-col overflow-hidden" data-testid="maintenance-report-dialog">
        <DialogHeader className="px-6 py-4 border-b bg-gradient-to-r from-emerald-600 to-teal-600 text-white flex-none">
          <div className="flex items-center justify-between gap-4">
            <DialogTitle className="text-2xl font-bold text-white">Maintenance Report</DialogTitle>
            <div className="flex items-center gap-2">
              <select
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value))}
                className="bg-white/95 text-slate-900 text-sm rounded px-2 py-1.5 border-0 focus:ring-2 focus:ring-white/50"
                data-testid="report-month-select"
              >
                {monthNames.map((m, i) => (
                  <option key={i} value={i + 1}>{m}</option>
                ))}
              </select>
              <select
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                className="bg-white/95 text-slate-900 text-sm rounded px-2 py-1.5 border-0 focus:ring-2 focus:ring-white/50"
                data-testid="report-year-select"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20"
                onClick={load}
                disabled={loading}
                data-testid="report-refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary" size="sm" className="gap-1" data-testid="report-export-btn">
                    <Download className="w-4 h-4" /> Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => downloadCsv('amc_breakdown')}>AMC vs Non-AMC (CSV)</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => downloadCsv('repair_items')}>Repair Items — detail (CSV)</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => downloadCsv('in_house')}>In-house Recovery (CSV)</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => downloadCsv('external')}>External Claims (CSV)</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-50">
          {loading && !data && (
            <div className="text-center py-20 text-slate-500">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading report…
            </div>
          )}
          {data && (
            <>
              {/* KPI cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="report-kpis">
                <KpiCard label="Total Spend" value={data.kpis.total_cost} trend={data.kpis.trends.total_cost} invertGood icon={Wallet} accent="blue" />
                <KpiCard label="Total Qty" value={data.kpis.total_qty} isCurrency={false} trend={data.kpis.trends.total_qty} invertGood icon={Package} accent="violet" />
                <KpiCard label="Admin Discount" value={data.kpis.admin_discount} trend={data.kpis.trends.admin_discount} icon={Receipt} accent="amber" />
                <KpiCard label="Recovered" value={data.kpis.total_recovered} trend={data.kpis.trends.total_recovered} icon={Wallet} accent="emerald" />
                <KpiCard label="Pending Recovery" value={data.kpis.pending_recovery} trend={data.kpis.trends.pending_recovery} invertGood icon={Wallet} accent="rose" />
                <KpiCard label="AMC Spend" value={data.kpis.amc_spend} trend={data.kpis.trends.amc_spend} invertGood icon={PieChart} accent="blue" />
              </div>
              <p className="text-xs text-slate-500 -mt-4">Trend vs {monthNames[(month + 10) % 12]} {month === 1 ? year - 1 : year} · green = better, red = worse.</p>

              {/* Repair Item Consumption */}
              <Card>
                <CardContent className="p-0">
                  <div className="px-4 py-3 border-b flex items-center justify-between">
                    <h3 className="font-semibold text-slate-900">Repair Item Consumption</h3>
                    <Badge variant="outline" className="text-xs">{data.repair_items.length} items</Badge>
                  </div>
                  <div className="overflow-x-auto max-h-[420px]">
                    <Table>
                      <TableHeader className="sticky top-0 bg-slate-100 z-10">
                        <TableRow>
                          <TableHead>Repair Item</TableHead>
                          <TableHead className="text-center">AMC?</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead className="text-right">Total Spend</TableHead>
                          <TableHead className="text-right">Trend (spend)</TableHead>
                          <TableHead>Top Bikes</TableHead>
                          <TableHead>Top Riders</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.repair_items.length === 0 ? (
                          <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-6">No repair items this month.</TableCell></TableRow>
                        ) : data.repair_items.map((ri) => (
                          <TableRow
                            key={ri.item_id || ri.item_name}
                            data-testid={`report-repair-row-${ri.item_id || ri.item_name}`}
                            className="cursor-pointer hover:bg-emerald-50/60"
                            onClick={() => openItemHistory(ri)}
                            title="Click to view replacement history across all bikes"
                          >
                            <TableCell>
                              <div className="flex items-center gap-1.5">
                                <div>
                                  <div className="text-sm font-medium">{ri.item_name}</div>
                                  {ri.item_code && <div className="text-[11px] text-slate-500 font-mono">{ri.item_code}</div>}
                                </div>
                                <History className="w-3.5 h-3.5 text-emerald-600 opacity-70 shrink-0" />
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              {ri.is_amc
                                ? <Badge className="bg-blue-100 text-blue-700 border-blue-200 border">AMC</Badge>
                                : <Badge variant="outline" className="text-slate-600">Non-AMC</Badge>}
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              {ri.qty_used}
                              <div className="mt-0.5"><TrendPill pct={ri.qty_trend_pct} invertGood /></div>
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              <CurrencyDisplay amount={ri.total_spend} />
                              <div className="mt-0.5 text-[11px] text-slate-500">prev <CurrencyDisplay amount={ri.spend_prev} /></div>
                            </TableCell>
                            <TableCell className="text-right"><TrendPill pct={ri.spend_trend_pct} invertGood size="lg" /></TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {ri.top_bikes.map((b, i) => (
                                  <Badge key={i} variant="outline" className="text-[11px]">
                                    {b.plate} <span className="text-slate-500 ml-1">×{b.count}</span>
                                  </Badge>
                                ))}
                                {ri.top_bikes.length === 0 && <span className="text-slate-400 text-xs">—</span>}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {ri.top_riders.map((r, i) => (
                                  <Badge key={i} variant="outline" className="text-[11px]">
                                    {r.name} <span className="text-slate-500 ml-1">×{r.count}</span>
                                  </Badge>
                                ))}
                                {ri.top_riders.length === 0 && <span className="text-slate-400 text-xs">—</span>}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              {/* AMC vs Non-AMC breakdown */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-slate-900">AMC vs Non-AMC Breakdown</h3>
                    <Button variant="outline" size="sm" onClick={() => downloadCsv('amc_breakdown')} data-testid="report-amc-csv-btn">
                      <Download className="w-3.5 h-3.5 mr-1" /> CSV
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-lg border p-4 bg-gradient-to-br from-blue-50 to-blue-100/40">
                      <div className="text-sm font-medium text-blue-800 mb-2">AMC Items</div>
                      <div className="flex items-baseline justify-between">
                        <span className="text-2xl font-bold text-blue-900"><CurrencyDisplay amount={data.amc_breakdown.amc_spend} /></span>
                        <span className="text-sm text-blue-700">{data.amc_breakdown.amc_qty} units</span>
                      </div>
                      <div className="mt-1 text-xs text-blue-700">{data.amc_breakdown.amc_share}% of gross spend</div>
                    </div>
                    <div className="rounded-lg border p-4 bg-gradient-to-br from-slate-50 to-slate-100/50">
                      <div className="text-sm font-medium text-slate-800 mb-2">Non-AMC Items</div>
                      <div className="flex items-baseline justify-between">
                        <span className="text-2xl font-bold text-slate-900"><CurrencyDisplay amount={data.amc_breakdown.non_amc_spend} /></span>
                        <span className="text-sm text-slate-700">{data.amc_breakdown.non_amc_qty} units</span>
                      </div>
                      <div className="mt-1 text-xs text-slate-700">{(100 - data.amc_breakdown.amc_share).toFixed(1)}% of gross spend</div>
                    </div>
                  </div>
                  {/* Simple horizontal bar */}
                  <div className="mt-4">
                    <div className="h-3 rounded-full bg-slate-200 overflow-hidden flex">
                      <div className="bg-blue-500 h-full" style={{ width: `${data.amc_breakdown.amc_share}%` }} />
                      <div className="bg-slate-500 h-full" style={{ width: `${100 - data.amc_breakdown.amc_share}%` }} />
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-2 italic">
                    Note: AMC / Non-AMC split reflects gross line-item value (rate × quantity). Total Spend in the KPI cards is net of admin discount.
                  </p>
                </CardContent>
              </Card>

              {/* Cost Recovery — In-house */}
              <Card>
                <CardContent className="p-0">
                  <div className="px-4 py-3 border-b flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-900">Cost Recovery — In-house Lessee</h3>
                      <p className="text-xs text-slate-500 mt-0.5">From Talent PMS recovery — click a row to expand per-installment history.</p>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="text-slate-600">Cost: <b className="text-slate-900"><CurrencyDisplay amount={data.recoveries.in_house_totals.cost} /></b></span>
                      <span className="text-slate-600">Discount: <b className="text-amber-700"><CurrencyDisplay amount={data.recoveries.in_house_totals.discount} /></b></span>
                      <span className="text-slate-600">Recovered: <b className="text-emerald-700"><CurrencyDisplay amount={data.recoveries.in_house_totals.recovered} /></b></span>
                      <span className="text-slate-600">Outstanding: <b className="text-rose-700"><CurrencyDisplay amount={data.recoveries.in_house_totals.outstanding} /></b></span>
                      <Button variant="outline" size="sm" onClick={() => downloadCsv('in_house')} data-testid="report-inhouse-csv-btn">
                        <Download className="w-3.5 h-3.5 mr-1" /> CSV
                      </Button>
                    </div>
                  </div>
                  <div className="overflow-x-auto max-h-[420px]">
                    <Table>
                      <TableHeader className="sticky top-0 bg-slate-100 z-10">
                        <TableRow>
                          <TableHead className="w-6"></TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Bike</TableHead>
                          <TableHead>Rider</TableHead>
                          <TableHead>Lessee</TableHead>
                          <TableHead className="text-right">Cost</TableHead>
                          <TableHead className="text-right">Admin Disc.</TableHead>
                          <TableHead className="text-right">Final</TableHead>
                          <TableHead className="text-right">Recovered</TableHead>
                          <TableHead className="text-right">Outstanding</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.recoveries.in_house.length === 0 ? (
                          <TableRow><TableCell colSpan={11} className="text-center text-slate-400 py-6">No in-house maintenance this month.</TableCell></TableRow>
                        ) : data.recoveries.in_house.map((r) => {
                          const expanded = expandedIds.has(r.maintenance_id);
                          const hasHistory = (r.recovery_history || []).length > 0;
                          return (
                            <>
                              <TableRow key={r.maintenance_id} className="hover:bg-slate-50 cursor-pointer"
                                onClick={() => hasHistory && toggleRow(r.maintenance_id)}
                                data-testid={`report-inhouse-row-${r.maintenance_id}`}>
                                <TableCell>
                                  {hasHistory ? (expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />) : null}
                                </TableCell>
                                <TableCell className="text-xs whitespace-nowrap">{r.maintenance_date}</TableCell>
                                <TableCell className="text-xs font-mono">{r.bike_plate}</TableCell>
                                <TableCell className="text-xs">{r.rider_name}</TableCell>
                                <TableCell className="text-xs">{r.lessee_name}</TableCell>
                                <TableCell className="text-right text-xs"><CurrencyDisplay amount={r.total_cost} /></TableCell>
                                <TableCell className="text-right text-xs text-amber-700">
                                  {r.admin_discount > 0 ? <CurrencyDisplay amount={r.admin_discount} /> : '-'}
                                  {r.admin_discount_reason && (
                                    <div className="text-[10px] text-slate-500 italic truncate max-w-[120px]">{r.admin_discount_reason}</div>
                                  )}
                                </TableCell>
                                <TableCell className="text-right text-xs font-semibold"><CurrencyDisplay amount={r.final_amount} /></TableCell>
                                <TableCell className="text-right text-xs text-emerald-700 font-semibold"><CurrencyDisplay amount={r.recovered_amount} /></TableCell>
                                <TableCell className="text-right text-xs text-rose-700 font-semibold"><CurrencyDisplay amount={r.outstanding} /></TableCell>
                                <TableCell className="text-xs">
                                  {r.payment_status === 'recovered_from_salary' || r.payment_status === 'recovered' || r.payment_status === 'paid'
                                    ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 border text-[10px]">Recovered</Badge>
                                    : <Badge className="bg-amber-100 text-amber-700 border-amber-200 border text-[10px]">Pending</Badge>}
                                </TableCell>
                              </TableRow>
                              {expanded && hasHistory && (
                                <TableRow className="bg-slate-50">
                                  <TableCell colSpan={11} className="p-0">
                                    <div className="p-3">
                                      <div className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1">
                                        <Users className="w-3 h-3" /> Recovery History — per-installment events
                                      </div>
                                      <Table>
                                        <TableHeader>
                                          <TableRow className="text-[11px]">
                                            <TableHead className="h-8">Salary Month</TableHead>
                                            <TableHead className="h-8">Date Applied</TableHead>
                                            <TableHead className="h-8 text-right">Amount</TableHead>
                                            <TableHead className="h-8 text-right">Balance After</TableHead>
                                            <TableHead className="h-8">Source</TableHead>
                                            <TableHead className="h-8">Note</TableHead>
                                          </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                          {r.recovery_history.map((h, i) => (
                                            <TableRow key={i} className="text-xs">
                                              <TableCell>{h.salary_month || h.payroll_month || '-'}</TableCell>
                                              <TableCell>{h.applied_date || h.date || '-'}</TableCell>
                                              <TableCell className="text-right font-semibold text-emerald-700"><CurrencyDisplay amount={h.amount || 0} /></TableCell>
                                              <TableCell className="text-right"><CurrencyDisplay amount={h.balance_after || 0} /></TableCell>
                                              <TableCell>{h.source || 'salary'}</TableCell>
                                              <TableCell className="text-[11px] text-slate-500">{h.note || h.remarks || ''}</TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              {/* Cost Recovery — External */}
              <Card>
                <CardContent className="p-0">
                  <div className="px-4 py-3 border-b flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-900">Cost Recovery — External Lessee</h3>
                      <p className="text-xs text-slate-500 mt-0.5">Claim amounts billed to the external lessee.</p>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="text-slate-600">Claim total: <b className="text-slate-900"><CurrencyDisplay amount={data.recoveries.external_totals.claim} /></b></span>
                      <span className="text-slate-600">Received: <b className="text-emerald-700"><CurrencyDisplay amount={data.recoveries.external_totals.recovered} /></b></span>
                      <span className="text-slate-600">Outstanding: <b className="text-rose-700"><CurrencyDisplay amount={data.recoveries.external_totals.outstanding} /></b></span>
                      <Button variant="outline" size="sm" onClick={() => downloadCsv('external')} data-testid="report-external-csv-btn">
                        <Download className="w-3.5 h-3.5 mr-1" /> CSV
                      </Button>
                    </div>
                  </div>
                  <div className="overflow-x-auto max-h-[380px]">
                    <Table>
                      <TableHeader className="sticky top-0 bg-slate-100 z-10">
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Bike</TableHead>
                          <TableHead>Lessee</TableHead>
                          <TableHead className="text-right">Cost</TableHead>
                          <TableHead className="text-right">Admin Discount</TableHead>
                          <TableHead className="text-right">Claim Amount</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.recoveries.external.length === 0 ? (
                          <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-6">No external maintenance this month.</TableCell></TableRow>
                        ) : data.recoveries.external.map((r) => (
                          <TableRow key={r.maintenance_id} data-testid={`report-external-row-${r.maintenance_id}`}>
                            <TableCell className="text-xs whitespace-nowrap">{r.maintenance_date}</TableCell>
                            <TableCell className="text-xs font-mono">{r.bike_plate}</TableCell>
                            <TableCell className="text-xs">{r.lessee_name}</TableCell>
                            <TableCell className="text-right text-xs"><CurrencyDisplay amount={r.total_cost} /></TableCell>
                            <TableCell className="text-right text-xs text-amber-700">
                              {r.admin_discount > 0 ? <CurrencyDisplay amount={r.admin_discount} /> : '-'}
                            </TableCell>
                            <TableCell className="text-right text-xs font-semibold text-blue-700"><CurrencyDisplay amount={r.claim_amount} /></TableCell>
                            <TableCell className="text-xs">
                              {r.payment_status === 'recovered_from_salary' || r.payment_status === 'recovered' || r.payment_status === 'paid'
                                ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 border text-[10px]">Received</Badge>
                                : <Badge className="bg-amber-100 text-amber-700 border-amber-200 border text-[10px]">Pending</Badge>}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </DialogContent>

      {/* Repair Item Replacement History — cross-bike history for a
          selected repair item within the current month. Shows the
          previous replacement of the same item on the same bike, and
          the KM covered since (helps gauge spare-part life). */}
      <Dialog open={!!historyItem} onOpenChange={(v) => { if (!v) closeItemHistory(); }}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-emerald-600" />
              {historyItem?.item_name || 'Replacement History'}
              {historyItem?.item_code && (
                <span className="text-xs font-mono text-slate-500">({historyItem.item_code})</span>
              )}
              <span className="text-xs font-normal text-slate-500 ml-2">
                {monthNames[month - 1]} {year}
              </span>
            </DialogTitle>
          </DialogHeader>
          {historyLoading ? (
            <div className="py-8 text-center text-slate-500 text-sm">Loading…</div>
          ) : historyData ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                <div className="rounded border p-2 bg-slate-50">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">Replacements</div>
                  <div className="text-lg font-bold text-slate-900">{historyData.totals.replacements}</div>
                </div>
                <div className="rounded border p-2 bg-slate-50">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">Total Qty</div>
                  <div className="text-lg font-bold text-slate-900">{historyData.totals.qty}</div>
                </div>
                <div className="rounded border p-2 bg-slate-50">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">Total Spend</div>
                  <div className="text-lg font-bold text-slate-900"><CurrencyDisplay amount={historyData.totals.amount} /></div>
                </div>
                <div className="rounded border p-2 bg-emerald-50">
                  <div className="text-[10px] uppercase tracking-wide text-emerald-700">Avg KM between replacements</div>
                  <div className="text-lg font-bold text-emerald-900">
                    {historyData.totals.avg_km_between_replacements !== null
                      ? `${historyData.totals.avg_km_between_replacements.toLocaleString()} km`
                      : '—'}
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto max-h-[55vh] border rounded">
                <Table>
                  <TableHeader className="sticky top-0 bg-slate-100 z-10">
                    <TableRow>
                      <TableHead className="text-xs">Date</TableHead>
                      <TableHead className="text-xs">Bike Plate</TableHead>
                      <TableHead className="text-xs">Rider</TableHead>
                      <TableHead className="text-xs text-right">Odometer (this)</TableHead>
                      <TableHead className="text-xs">Previous replaced</TableHead>
                      <TableHead className="text-xs text-right">Odometer (prev)</TableHead>
                      <TableHead className="text-xs text-right">KM covered</TableHead>
                      <TableHead className="text-xs text-right">Qty</TableHead>
                      <TableHead className="text-xs text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {historyData.rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center text-slate-400 py-8 text-sm">
                          No replacements recorded for this item in {monthNames[month - 1]} {year}.
                        </TableCell>
                      </TableRow>
                    ) : historyData.rows.map((row, i) => (
                      <TableRow key={`${row.maintenance_id}-${i}`}>
                        <TableCell className="text-xs whitespace-nowrap">{row.maintenance_date}</TableCell>
                        <TableCell className="text-xs font-mono">{row.bike_plate}</TableCell>
                        <TableCell className="text-xs">{row.rider_name}</TableCell>
                        <TableCell className="text-right text-xs">
                          {row.odometer_km !== null && row.odometer_km !== undefined
                            ? `${row.odometer_km.toLocaleString()} km`
                            : <span className="text-slate-400">—</span>}
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap">
                          {row.prev_date || <span className="text-slate-400">First time</span>}
                        </TableCell>
                        <TableCell className="text-right text-xs">
                          {row.prev_odometer_km !== null && row.prev_odometer_km !== undefined
                            ? `${row.prev_odometer_km.toLocaleString()} km`
                            : <span className="text-slate-400">—</span>}
                        </TableCell>
                        <TableCell className="text-right text-xs">
                          {row.km_covered !== null && row.km_covered !== undefined ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 font-semibold">
                              {row.km_covered.toLocaleString()} km
                            </span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right text-xs">{row.qty}</TableCell>
                        <TableCell className="text-right text-xs font-semibold">
                          <CurrencyDisplay amount={row.amount} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="text-[11px] text-slate-500 mt-2 italic">
                <b>KM covered</b> is calculated as <i>this odometer − previous odometer</i> for the same
                spare on the same bike. A missing value means either the previous replacement isn&apos;t
                recorded or the odometer wasn&apos;t captured. Encourage technicians to always record the
                odometer during maintenance for accurate spare-life tracking.
              </p>
              <div className="flex justify-end mt-3">
                <Button variant="outline" size="sm" onClick={closeItemHistory}>
                  <X className="w-3.5 h-3.5 mr-1" /> Close
                </Button>
              </div>
            </>
          ) : (
            <div className="py-8 text-center text-slate-500 text-sm">No data.</div>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default MaintenanceReport;
